import React, { useEffect, useState } from 'react';
import { X, Wifi, WifiOff, Network, Power, Clock, Activity, Database, Cpu, Signal, Globe, Server, Plug, BatteryMedium, AlertTriangle, Download } from 'lucide-react';
import {
  useGetBaseUnitTelemetryQuery,
  useGetBaseFirmwareLatestQuery,
  useUpdateBaseFirmwareMutation,
  useGetRepeaterConversionInfoQuery,
  useConvertBaseToRepeaterMutation,
} from '../../services/baseUnitTelemetryApi';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { usePermissions } from '../../hooks/usePermissions';
import { isNewerVersion } from '../../utils/sensorUtils';

const SUPER_ADMIN_EMAILS = ['adam@thermal-systems.co.uk', 'dave@thermal-systems.co.uk'];
// Bases older than this have no OTA partition (huge_app) and can't be converted remotely
const MIN_OTA_BASE_FIRMWARE = '1.1.0';

interface BaseUnitTelemetryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  baseUnit: {
    id: number;
    serialNo: number;
    base_unit_name: string;
    type?: number;
  } | null;
}

// Telemetry older than this is treated as stale / unit considered offline.
// Matches the CommsCell threshold on BaseUnitsPage.
const STALE_THRESHOLD_MS = 60 * 60 * 1000;

// After an update command, block repeats until the base reports the new version
// or this long has passed (download + reboot + first telemetry takes a few minutes).
const OTA_COOLDOWN_MS = 5 * 60 * 1000;
const OTA_POLL_MS = 20 * 1000;

interface PendingOta {
  version: string;
  sentAt: number;
}

// Remembered per browser so closing the dialog or reloading the page doesn't re-enable the button
const pendingOtaKey = (serial: string) => `ts1.baseOta.${serial}`;

const readPendingOta = (serial: string): PendingOta | null => {
  try {
    const raw = localStorage.getItem(pendingOtaKey(serial));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingOta;
    return parsed && typeof parsed.sentAt === 'number' ? parsed : null;
  } catch {
    return null;
  }
};

const writePendingOta = (serial: string, pending: PendingOta | null) => {
  try {
    if (pending) localStorage.setItem(pendingOtaKey(serial), JSON.stringify(pending));
    else localStorage.removeItem(pendingOtaKey(serial));
  } catch {
    // storage unavailable (private mode etc.) - the in-memory state still applies
  }
};

const formatRelativeAge = (ms: number) => {
  if (ms < 60 * 1000) return 'less than a minute ago';
  const minutes = Math.floor(ms / (60 * 1000));
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

export default function BaseUnitTelemetryDialog({ isOpen, onClose, baseUnit }: BaseUnitTelemetryDialogProps) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Only fetch telemetry for TS1 base units (type 1)
  const isTS1BaseUnit = baseUnit?.type === 1;
  const serial = baseUnit?.serialNo.toString() || '';

  // Pending firmware update (cooldown) for this base
  const [pendingOta, setPendingOta] = useState<PendingOta | null>(null);
  const [updateResult, setUpdateResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (isOpen && serial) setPendingOta(readPendingOta(serial));
  }, [isOpen, serial]);
  const otaCooldownLeftMs = pendingOta ? Math.max(0, pendingOta.sentAt + OTA_COOLDOWN_MS - now) : 0;
  const otaWaiting = otaCooldownLeftMs > 0;
  useEffect(() => {
    if (!isOpen || !otaWaiting) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isOpen, otaWaiting]);

  const {
    data: telemetryData,
    isLoading,
    error,
    refetch
  } = useGetBaseUnitTelemetryQuery(serial, {
    skip: !isOpen || !baseUnit || !isTS1BaseUnit,
    // While an update is in progress, poll so the new version shows up without a manual refresh
    pollingInterval: otaWaiting ? OTA_POLL_MS : 0,
  });

  // The base has reported the version we sent it to: the update is done
  useEffect(() => {
    if (pendingOta && telemetryData?.firmware_version &&
        !isNewerVersion(pendingOta.version, telemetryData.firmware_version)) {
      writePendingOta(serial, null);
      setPendingOta(null);
      setUpdateResult({ ok: true, text: `Update complete: the base is now running ${telemetryData.firmware_version}.` });
    }
  }, [pendingOta, telemetryData?.firmware_version, serial]);

  const telemetryAgeMs = telemetryData
    ? Math.max(0, Date.now() - new Date(telemetryData.timestamp).getTime())
    : 0;
  const isStale = !!telemetryData && telemetryAgeMs > STALE_THRESHOLD_MS;

  // Firmware OTA
  const { canManageBaseUnits } = usePermissions();
  const { data: firmwareInfo } = useGetBaseFirmwareLatestQuery(serial, {
    skip: !isOpen || !baseUnit || !isTS1BaseUnit,
  });
  const [updateBaseFirmware, { isLoading: isSendingUpdate }] = useUpdateBaseFirmwareMutation();
  const [confirmUpdate, setConfirmUpdate] = useState(false);
  const latestFirmware = firmwareInfo?.latestVersion;
  const updateAvailable = !!telemetryData && !!latestFirmware &&
    isNewerVersion(latestFirmware, telemetryData.firmware_version || '');

  const handleFirmwareUpdate = async () => {
    if (!baseUnit || !latestFirmware || otaWaiting) return;
    setUpdateResult(null);
    try {
      await updateBaseFirmware(serial).unwrap();
      const pending = { version: latestFirmware, sentAt: Date.now() };
      writePendingOta(serial, pending);
      setPendingOta(pending);
      setNow(pending.sentAt);
    } catch (err) {
      const message = (err as { data?: { message?: string } })?.data?.message;
      setUpdateResult({ ok: false, text: message || 'Failed to send the update command.' });
    } finally {
      setConfirmUpdate(false);
    }
  };

  // Super admin: convert this base into a repeater over OTA
  const authUser = useSelector((state: RootState) => state.auth.user);
  const isSuperAdmin = SUPER_ADMIN_EMAILS.includes((authUser?.email ?? '').toLowerCase());
  const { data: repeaterInfo, refetch: refetchRepeaterInfo } = useGetRepeaterConversionInfoQuery(serial, {
    skip: !isOpen || !baseUnit || !isTS1BaseUnit || !isSuperAdmin,
  });
  const [convertBaseToRepeater, { isLoading: isConverting }] = useConvertBaseToRepeaterMutation();
  const [confirmConvert, setConfirmConvert] = useState(false);
  const [convertConfirmText, setConvertConfirmText] = useState('');
  const [convertResult, setConvertResult] = useState<{ ok: boolean; text: string } | null>(null);
  const canOtaConvert = !!telemetryData?.firmware_version &&
    !isNewerVersion(MIN_OTA_BASE_FIRMWARE, telemetryData.firmware_version);

  const handleConvertToRepeater = async () => {
    if (!baseUnit) return;
    setConvertResult(null);
    try {
      const r = await convertBaseToRepeater(serial).unwrap();
      setConvertResult({
        ok: true,
        text: `Sent. The base downloads repeater ${r.repeaterVersion} and reboots in 1–3 minutes, then stops reporting as a base. ` +
          `It comes back as repeater ${r.repeaterId}; its first beacon is within about 6 minutes (Super Admin → Sensor Routes).`,
      });
    } catch (err) {
      const message = (err as { data?: { message?: string } })?.data?.message;
      setConvertResult({ ok: false, text: message || 'Failed to send the conversion.' });
    } finally {
      setConfirmConvert(false);
      setConvertConfirmText('');
      refetchRepeaterInfo();
    }
  };

  const handleRefresh = async () => {
    if (!isTS1BaseUnit) return;
    
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatUptime = (hours: number, minutes: number, seconds: number) => {
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // When telemetry is stale we render every "live" indicator in neutral grey
  // so we don't imply the unit is currently online / connected / on mains.
  const staleColor = 'text-gray-500 dark:text-gray-400';

  const getSignalStrength = (dbm: number) => {
    if (isStale) return { level: 'Last reported', color: staleColor };
    if (dbm >= -30) return { level: 'Excellent', color: 'text-green-600 dark:text-green-400' };
    if (dbm >= -50) return { level: 'Good', color: 'text-green-500 dark:text-green-400' };
    if (dbm >= -70) return { level: 'Fair', color: 'text-yellow-500 dark:text-yellow-400' };
    if (dbm >= -80) return { level: 'Weak', color: 'text-orange-500 dark:text-orange-400' };
    return { level: 'Poor', color: 'text-red-500 dark:text-red-400' };
  };

  const getPowerState = (status: string) => {
    const s = status?.toLowerCase() ?? '';
    if (s.includes('usb') || s.includes('mains')) {
      return {
        label: isStale ? 'Last reported: Mains (USB)' : 'Mains (USB)',
        color: isStale ? staleColor : 'text-green-600 dark:text-green-400',
        Icon: Plug,
      };
    }
    if (s.includes('bat')) {
      return {
        label: isStale ? 'Last reported: Battery' : 'Battery',
        color: isStale ? staleColor : 'text-amber-500 dark:text-amber-400',
        Icon: BatteryMedium,
      };
    }
    return {
      label: status || 'Unknown',
      color: isStale ? staleColor : 'text-gray-600 dark:text-gray-400',
      Icon: Power,
    };
  };

  // Rough charge left for the base's single Li-ion cell, from its resting voltage
  const batteryPercent = (v: number) => {
    const curve: [number, number][] = [
      [4.2, 100], [4.1, 90], [4.0, 79], [3.9, 68], [3.8, 56], [3.7, 42], [3.6, 25], [3.5, 12], [3.4, 5], [3.3, 0],
    ];
    if (v >= curve[0][0]) return 100;
    for (let i = 1; i < curve.length; i++) {
      const [v1, p1] = curve[i - 1];
      const [v2, p2] = curve[i];
      if (v >= v2) return Math.round(p2 + ((v - v2) / (v1 - v2)) * (p1 - p2));
    }
    return 0;
  };

  const getBatteryColor = (v: number) => {
    if (isStale) return staleColor;
    if (v >= 3.7) return 'text-green-600 dark:text-green-400';
    if (v >= 3.3) return 'text-amber-500 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  };

  const liveStateColor = (live: boolean) =>
    isStale
      ? staleColor
      : live
        ? 'text-green-600 dark:text-green-400'
        : 'text-red-600 dark:text-red-400';

  const liveStateLabel = (live: boolean, onLabel: string, offLabel: string) =>
    isStale
      ? `Last reported: ${live ? onLabel : offLabel}`
      : live
        ? onLabel
        : offLabel;

  if (!isOpen || !baseUnit) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-app-panel dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Base Unit Telemetry
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {baseUnit.base_unit_name} ({baseUnit.serialNo})
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {isTS1BaseUnit && (
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="px-3 py-1 text-sm bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-md flex items-center"
              >
                <Activity className={`h-4 w-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {!isTS1BaseUnit ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Server className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Telemetry Not Available
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md">
                  Telemetry data is only available for TS1 base units. This base unit ({baseUnit?.base_unit_name}) 
                  is a {baseUnit?.type === 2 ? 'Hybrid EMS' : 'different type'} and doesn't support telemetry monitoring.
                </p>
              </div>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="ml-2 text-gray-600 dark:text-gray-400">Loading telemetry data...</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-md mb-6">
              <p className="text-sm text-red-600 dark:text-red-400">
                Failed to load telemetry data. Please try again.
              </p>
            </div>
          ) : telemetryData ? (
            <div className="space-y-6">
              {isStale && (
                <div className="flex items-start gap-3 p-4 rounded-md border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/20">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" aria-hidden />
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                      This base unit appears to be offline
                    </h3>
                    <p className="text-sm text-amber-700 dark:text-amber-200/90 mt-1">
                      The values below are the last reported telemetry from{' '}
                      <span className="font-medium">{new Date(telemetryData.timestamp).toLocaleString()}</span>
                      {' '}({formatRelativeAge(telemetryAgeMs)}) and do not reflect the unit's current state.
                    </p>
                  </div>
                </div>
              )}

              {/* Device Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                    <Cpu className="h-5 w-5 mr-2" />
                    Device Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Serial Number:</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{telemetryData.device_serial}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Firmware Version:</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{telemetryData.firmware_version}</span>
                    </div>
                    {latestFirmware && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Latest Available:</span>
                        <span className={`text-sm font-medium ${updateAvailable ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                          {latestFirmware}{updateAvailable ? ' (update available)' : ' (up to date)'}
                        </span>
                      </div>
                    )}
                    {canManageBaseUnits && updateAvailable && otaWaiting && pendingOta && (
                      <div className="p-3 rounded-md border border-indigo-200 dark:border-indigo-800/60 bg-indigo-50 dark:bg-indigo-900/20">
                        <p className="text-sm text-indigo-800 dark:text-indigo-200 flex items-center">
                          <Activity className="h-4 w-4 mr-1 animate-spin flex-shrink-0" />
                          Updating to {pendingOta.version}: waiting for the base to reboot and report in.
                        </p>
                        <p className="text-xs text-indigo-700 dark:text-indigo-300 mt-1">
                          Sent {formatRelativeAge(now - pendingOta.sentAt)}. Checking automatically; the button comes back in{' '}
                          {Math.floor(otaCooldownLeftMs / 60000)}:{String(Math.floor((otaCooldownLeftMs % 60000) / 1000)).padStart(2, '0')}{' '}
                          if the new version hasn't been reported.
                        </p>
                      </div>
                    )}
                    {canManageBaseUnits && updateAvailable && !otaWaiting && (
                      <div className="pt-1">
                        {pendingOta && (
                          <p className="text-xs text-amber-700 dark:text-amber-300 mb-2">
                            The base hasn't reported {pendingOta.version} yet. Check it is online before trying again.
                          </p>
                        )}
                        {!confirmUpdate ? (
                          <button
                            onClick={() => { setUpdateResult(null); setConfirmUpdate(true); }}
                            disabled={isStale}
                            title={isStale ? 'The base unit appears to be offline' : `Update firmware to ${latestFirmware}`}
                            className="w-full px-3 py-1.5 text-sm bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md flex items-center justify-center"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Update firmware to {latestFirmware}
                          </button>
                        ) : (
                          <div className="p-3 rounded-md border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/20">
                            <p className="text-sm text-amber-800 dark:text-amber-200 mb-2">
                              The base stops forwarding sensor readings while it downloads and reboots (about 1–3 minutes). Update now?
                            </p>
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => setConfirmUpdate(false)}
                                disabled={isSendingUpdate}
                                className="px-3 py-1 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleFirmwareUpdate}
                                disabled={isSendingUpdate}
                                className="px-3 py-1 text-sm bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-md"
                              >
                                {isSendingUpdate ? 'Sending…' : 'Update now'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {updateResult && (
                      <p className={`text-sm ${updateResult.ok ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {updateResult.text}
                      </p>
                    )}
                    {isSuperAdmin && (
                      <div className="mt-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                          Super admin: convert to repeater
                        </p>
                        {!repeaterInfo?.repeaterVersion ? (
                          <p className="text-xs text-gray-500 dark:text-gray-400">No repeater firmware is published on the firmware server.</p>
                        ) : !canOtaConvert ? (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Needs base firmware {MIN_OTA_BASE_FIRMWARE} or later (OTA partition). This base needs a USB flash instead.
                          </p>
                        ) : repeaterInfo.conversionInProgress && repeaterInfo.conversionInProgress.expiresAt > Date.now() ? (
                          <p className="text-xs text-amber-700 dark:text-amber-300">
                            A conversion of base {repeaterInfo.conversionInProgress.serial} is in progress. Try again in a few minutes.
                          </p>
                        ) : !confirmConvert ? (
                          <button
                            onClick={() => { setConvertResult(null); setConfirmConvert(true); }}
                            disabled={isStale}
                            title={isStale ? 'The base unit appears to be offline' : `Install repeater firmware ${repeaterInfo.repeaterVersion}`}
                            className="w-full px-3 py-1.5 text-sm border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed rounded-md flex items-center justify-center"
                          >
                            <Signal className="h-4 w-4 mr-1" />
                            Convert to repeater {repeaterInfo.repeaterId} (fw {repeaterInfo.repeaterVersion})
                          </button>
                        ) : (
                          <div className="p-3 rounded-md border border-red-200 dark:border-red-800/60 bg-red-50 dark:bg-red-900/20">
                            <p className="text-sm text-red-800 dark:text-red-200 mb-2">
                              Base {serial} becomes repeater {repeaterInfo.repeaterId}. It stops being a base straight away: no WiFi, MQTT, telemetry
                              or remote updates. <strong>Turning it back into a base needs a USB cable.</strong> Type the serial to confirm.
                            </p>
                            <input
                              value={convertConfirmText}
                              onChange={(e) => setConvertConfirmText(e.target.value.trim())}
                              placeholder={serial}
                              className="w-full mb-2 px-2 py-1 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => { setConfirmConvert(false); setConvertConfirmText(''); }}
                                disabled={isConverting}
                                className="px-3 py-1 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleConvertToRepeater}
                                disabled={isConverting || convertConfirmText !== serial}
                                className="px-3 py-1 text-sm bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white rounded-md"
                              >
                                {isConverting ? 'Sending…' : 'Convert now'}
                              </button>
                            </div>
                          </div>
                        )}
                        {convertResult && (
                          <p className={`text-sm mt-2 ${convertResult.ok ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {convertResult.text}
                          </p>
                        )}
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Power Status:</span>
                      {(() => {
                        const { label, color, Icon } = getPowerState(telemetryData.power_status);
                        const v = telemetryData.battery_voltage;
                        const onBattery = telemetryData.power_status?.toLowerCase().includes('bat');
                        return (
                          <span className={`text-sm font-medium flex items-center ${onBattery && v != null ? getBatteryColor(v) : color}`}>
                            <Icon className="h-4 w-4 mr-1" />
                            {label}
                            {onBattery && v != null && ` · ${v.toFixed(2)} V (~${batteryPercent(v)}%)`}
                          </span>
                        );
                      })()}
                    </div>
                    {telemetryData.battery_voltage != null && !telemetryData.power_status?.toLowerCase().includes('bat') && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Battery Voltage:</span>
                        <span className={`text-sm font-medium flex items-center ${getBatteryColor(telemetryData.battery_voltage)}`}>
                          <BatteryMedium className="h-4 w-4 mr-1" />
                          {telemetryData.battery_voltage.toFixed(2)} V
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Last Update:</span>
                      <span
                        className={`text-sm font-medium ${
                          isStale
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-gray-900 dark:text-white'
                        }`}
                      >
                        {new Date(telemetryData.timestamp).toLocaleString()}
                        {isStale && (
                          <span className="ml-1 text-xs font-normal">
                            ({formatRelativeAge(telemetryAgeMs)})
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                    <Clock className="h-5 w-5 mr-2" />
                    System Status
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Uptime:</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatUptime(telemetryData.uptime_hours, telemetryData.uptime_minutes, telemetryData.uptime_seconds)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Free Memory:</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {(telemetryData.free_heap_bytes / 1024).toFixed(1)} KB
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Heap Fragmentation:</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {telemetryData.heap_fragmentation}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">LoRa Processing:</span>
                      <span className={`text-sm font-medium ${liveStateColor(telemetryData.lora_processing_enabled)}`}>
                        {liveStateLabel(telemetryData.lora_processing_enabled, 'Enabled', 'Disabled')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Network Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  {(() => {
                    const isEthernet = telemetryData.connection_type === 'ethernet' || telemetryData.ethernet_connected;
                    const isConnected = isEthernet ? !!telemetryData.ethernet_connected : telemetryData.wifi_connected;
                    return (
                      <>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                          {isEthernet
                            ? <Network className="h-5 w-5 mr-2" />
                            : <Wifi className="h-5 w-5 mr-2" />}
                          {isEthernet ? 'Ethernet Connection' : 'WiFi Connection'}
                        </h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Status:</span>
                            <span className={`text-sm font-medium flex items-center ${liveStateColor(isConnected)}`}>
                              {isConnected
                                ? (isEthernet ? <Network className="h-4 w-4 mr-1" /> : <Wifi className="h-4 w-4 mr-1" />)
                                : <WifiOff className="h-4 w-4 mr-1" />}
                              {liveStateLabel(isConnected, 'Connected', 'Disconnected')}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Type:</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                              {telemetryData.connection_type ?? 'WiFi'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">IP Address:</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {isEthernet ? (telemetryData.ethernet_ip ?? '—') : (telemetryData.wifi_ip ?? '—')}
                            </span>
                          </div>
                          {!isEthernet && (
                            <>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600 dark:text-gray-400">SSID:</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">{telemetryData.wifi_ssid ?? '—'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600 dark:text-gray-400">MAC Address:</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">{telemetryData.wifi_mac ?? '—'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Signal Strength:</span>
                                <span className={`text-sm font-medium flex items-center ${getSignalStrength(telemetryData.wifi_signal_dbm).color}`}>
                                  <Signal className="h-4 w-4 mr-1" />
                                  {telemetryData.wifi_signal_dbm} dBm ({getSignalStrength(telemetryData.wifi_signal_dbm).level})
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                    <Server className="h-5 w-5 mr-2" />
                    MQTT Connection
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Status:</span>
                      <span className={`text-sm font-medium flex items-center ${liveStateColor(telemetryData.mqtt_connected)}`}>
                        <Globe className="h-4 w-4 mr-1" />
                        {liveStateLabel(telemetryData.mqtt_connected, 'Connected', 'Disconnected')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Broker:</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{telemetryData.mqtt_broker}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Port:</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{telemetryData.mqtt_port}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Topic Base:</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white font-mono">{telemetryData.mqtt_topic_base}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Data Statistics */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                  <Database className="h-5 w-5 mr-2" />
                  Data Statistics
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                      {telemetryData.total_readings_received.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Readings</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {telemetryData.readings_this_hour}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Readings This Hour</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {telemetryData.num_sensors_configured}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Sensors Configured</div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
